import React, { useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowRight,
  CalendarCheck,
  Check,
  Circle,
  Clock,
  Sparkles,
  Video,
  Wallet,
} from 'lucide-react'
import useSetupMode from '../../hooks/useSetupMode'
import { writeDraft } from '../signup/signupState'
import './SetupModeWelcome.css'

function formatActivationDateTime(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d)
}

function personaNoun(effectivePersona) {
  if (effectivePersona === 'academy') return 'academy'
  return 'community'
}

const CALL_TOPICS = [
  { icon: Video, label: 'Camera setup options for your venues' },
  { icon: Wallet, label: 'Pricing and analyst allocation' },
  { icon: Sparkles, label: 'A walkthrough of your portal and first games' },
]

function StatusBadge({ children, tone = 'teal' }) {
  const tones = {
    teal: 'bg-[#71cdd9]/15 text-[#0e6e7a] ring-[#71cdd9]/30',
    amber: 'bg-amber-50 text-amber-800 ring-amber-200/80',
    emerald: 'bg-emerald-50 text-emerald-800 ring-emerald-200/80',
  }
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-semibold tracking-wide ring-1 ${tones[tone]}`}
    >
      {children}
    </span>
  )
}

function IconHero({ icon: Icon, tone = 'teal' }) {
  const tones = {
    teal: 'from-[#71cdd9]/25 to-teal-100/60 text-[#0e6e7a] ring-[#71cdd9]/25',
    amber: 'from-amber-100 to-orange-50 text-amber-700 ring-amber-200/60',
    emerald: 'from-emerald-100 to-teal-50 text-emerald-700 ring-emerald-200/60',
  }
  return (
    <div
      className={`mb-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ring-1 shadow-sm ${tones[tone]}`}
      aria-hidden
    >
      <Icon size={26} strokeWidth={1.75} />
    </div>
  )
}

export default function SetupModeWelcome({ persona }) {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const {
    fullName,
    persona: draftPersona,
    activationState,
    activationCallBookedAt,
    refresh,
  } = useSetupMode()

  const effectivePersona = persona || draftPersona || 'community'
  const noun = personaNoun(effectivePersona)

  const firstName = useMemo(() => {
    const w = fullName?.trim().split(/\s+/).filter(Boolean)[0]
    if (!w) return 'there'
    return w
  }, [fullName])

  const bookCallRoute = `/${effectivePersona === 'venue' ? 'venue-owner' : effectivePersona}/book-activation-call`

  const when = useMemo(() => formatActivationDateTime(activationCallBookedAt), [activationCallBookedAt])

  useEffect(() => {
    if (searchParams.get('booked') !== '1') return
    // BACKEND TODO: remove this dev shortcut once Calendly webhook is wired.
    writeDraft({
      activationCallBooked: true,
      activationCallBookedAt: new Date(Date.now() + 2 * 86400000).toISOString(),
    })
    refresh()
    const next = new URLSearchParams(searchParams)
    next.delete('booked')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams, refresh])

  const liveLabel = noun === 'academy' ? 'academy' : 'community'

  return (
    <div className="relative">
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-8 rounded-[32px] bg-gradient-to-br from-[#71cdd9]/18 via-amber-100/25 to-indigo-100/15 blur-2xl"
      />

      <div className="welcome-hero-card relative overflow-hidden rounded-2xl border border-[#71cdd9]/20 shadow-[0_12px_40px_-12px_rgba(113,205,217,0.35)]">
        <div className="px-6 sm:px-10 py-10 sm:py-12">
          {activationState === 'awaiting_booking' && (
            <>
              <StatusBadge tone="teal">
                <Sparkles size={13} aria-hidden />
                Almost there
              </StatusBadge>

              <IconHero icon={Sparkles} tone="teal" />

              <p className="text-[16px] sm:text-[17px] text-[#0e6e7a] font-medium mb-2">
                Hey {firstName} — great to have you here.
              </p>
              <h1 className="text-[34px] sm:text-[42px] font-semibold tracking-tight leading-[1.12] text-slate-900 mb-4">
                {noun === 'academy'
                  ? "Let's bring your academy to life."
                  : "Your community is ready to come alive."}
              </h1>
              <p className="text-[15px] sm:text-[16px] text-slate-600 max-w-[640px] leading-relaxed mb-8">
                One quick 30-minute call with our team and we&apos;ll handle the rest —
                cameras, stats, highlights, all set up for your first games.
              </p>

              <button
                type="button"
                onClick={() => navigate(bookCallRoute)}
                className="cta-shimmer welcome-hero-cta relative inline-flex h-[56px] items-center justify-center gap-2 rounded-xl px-8 text-[15px] font-semibold"
              >
                Book your activation call
                <ArrowRight size={16} className="opacity-90" aria-hidden />
              </button>

              <div className="mt-6 flex flex-wrap gap-2.5">
                {['30 min call', 'No pressure', 'We handle setup'].map((chip) => (
                  <span
                    key={chip}
                    className="inline-flex items-center rounded-full bg-white/80 px-3 py-1.5 text-[12px] font-medium text-slate-600 ring-1 ring-slate-200/80"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </>
          )}

          {activationState === 'call_booked' && (
            <>
              <IconHero icon={CalendarCheck} tone="amber" />

              <p className="text-[16px] sm:text-[17px] text-amber-800/90 font-medium mb-2">
                Nice one, {firstName}!
              </p>
              <h1 className="text-[34px] sm:text-[42px] font-semibold tracking-tight leading-[1.12] text-slate-900 mb-4">
                You&apos;re on the calendar.
              </h1>
              <p className="text-[15px] sm:text-[16px] text-slate-600 max-w-[640px] leading-relaxed mb-6">
                We&apos;re looking forward to meeting you and getting your {liveLabel} set up.
              </p>

              <div className="mb-8 inline-flex max-w-full items-center gap-3 rounded-xl bg-gradient-to-r from-amber-50 to-[#71cdd9]/10 px-4 py-3.5 ring-1 ring-amber-200/70">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-amber-100">
                  <Clock size={18} className="text-amber-700" aria-hidden />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-700/80">
                    Your call
                  </p>
                  <p className="text-[14.5px] font-semibold text-slate-800 leading-snug">
                    {when || 'Your scheduled time'}
                  </p>
                </div>
              </div>

              <div className="welcome-topic-card rounded-xl p-5 max-w-[720px]">
                <p className="text-[13px] font-semibold text-[#0e6e7a] mb-4">
                  What we&apos;ll cover together
                </p>
                <ul className="space-y-3">
                  {CALL_TOPICS.map(({ icon: Icon, label }) => (
                    <li key={label} className="flex items-start gap-3 text-[14px] text-slate-700 leading-snug">
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/90 text-[#0e6e7a] ring-1 ring-[#71cdd9]/20">
                        <Icon size={14} aria-hidden />
                      </span>
                      {label}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}

          {activationState === 'call_completed' && (
            <>
              <StatusBadge tone="emerald">
                <Check size={13} aria-hidden />
                Activation in progress
              </StatusBadge>

              <IconHero icon={Sparkles} tone="emerald" />

              <p className="text-[16px] sm:text-[17px] text-emerald-800/90 font-medium mb-2">
                You&apos;re in great hands, {firstName}.
              </p>
              <h1 className="text-[34px] sm:text-[42px] font-semibold tracking-tight leading-[1.12] text-slate-900 mb-4">
                We&apos;re getting everything ready.
              </h1>
              <p className="text-[15px] sm:text-[16px] text-slate-600 max-w-[640px] leading-relaxed mb-8">
                Your portal goes live within 5–7 days. Here&apos;s where things stand —
                we&apos;ll keep you posted every step of the way.
              </p>

              <div className="welcome-topic-card rounded-xl p-5 max-w-[720px]">
                <ul className="space-y-3.5 text-[14px]">
                  {[
                    { label: 'Activation call complete', done: true },
                    { label: 'Camera plan confirmed', done: true },
                    { label: 'Analyst onboarding', done: false },
                    { label: 'First game scheduled', done: false },
                  ].map((row) => (
                    <li key={row.label} className="flex items-start gap-3">
                      {row.done ? (
                        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                          <Check size={14} aria-hidden />
                        </span>
                      ) : (
                        <Circle className="mt-1 text-[#71cdd9]/50" size={18} aria-hidden />
                      )}
                      <span className={row.done ? 'text-slate-700 font-medium' : 'text-slate-600'}>
                        {row.label}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="mt-8 text-[14px] text-slate-600">
                Questions along the way?{' '}
                <a
                  href="mailto:support@statbox.co"
                  className="font-semibold text-[#0e6e7a] hover:underline underline-offset-4"
                >
                  We&apos;re always happy to help — support@statbox.co
                </a>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
